/**
 * CwResultRow — one Connected Search result row.
 *
 * Sprint CW-G TKT-1. Reusable across the full Connected Search page
 * and the global top-bar search dropdown. Dispatches on entity_type
 * for the icon + secondary metadata, but the row layout is uniform.
 *
 * Click semantics:
 *   - CM page / site → relative router navigation (handled by parent
 *     via onSelect callback so this component stays href-agnostic).
 *   - Provider files / issues / deals / etc. → external new-tab open
 *     of the row's web_url.
 */
import { type FC, type MouseEvent } from 'react';
import {
  Building2,
  DollarSign,
  File,
  FileText,
  Folder,
  Globe,
  MessageSquare,
  Ticket,
  User,
} from 'lucide-react';

export type CwEntityType =
  | 'file' | 'issue' | 'page' | 'site'
  | 'deal' | 'contact' | 'company' | 'ticket'
  // CW-SF-M3 TKT-9 — Salesforce entities
  | 'account' | 'lead' | 'opportunity'
  | 'case' | 'task' | 'event'
  // CW-SF-M4 TKT-1 — Tier-3 SObjects
  | 'knowledge' | 'email' | 'note'
  // CW-SF-M6 TKT-3 — opted-in Custom Objects share this entity_type;
  // resolveGroupTitle reads payload._sobject_type to surface the
  // per-object display name (e.g. "Salesforce Project").
  | 'custom';

export interface CwSearchHit {
  id: string;
  external_file_id: string;
  file_name: string;
  provider_type: string;
  is_folder: boolean;
  mime_type: string | null;
  file_size_bytes: number | null;
  last_modified_at: string | null;
  web_url: string;
  file_path: string;
  thumbnail_url?: string | null;
  parent_name: string | null;
  parent_url: string | null;
  entity_type: CwEntityType;
  owner?: { name: string; email: string; user_url: string } | null;
  tags?: string[] | null;
  vortex_tags?: string[] | null;
}

interface Props {
  hit: CwSearchHit;
  /** Optional active highlight (keyboard focus). */
  active?: boolean;
  /** Click handler — parent decides routing (internal vs new tab). */
  onSelect: (hit: CwSearchHit, event: MouseEvent) => void;
  /** Optional dom id for ARIA option pattern. */
  id?: string;
  /** Optional hover preview handlers (parent owns the preview state). */
  onHoverEnter?: (hit: CwSearchHit, e: { clientX: number; clientY: number }) => void;
  onHoverMove?: (e: { clientX: number; clientY: number }) => void;
  onHoverLeave?: () => void;
}

function renderIcon(entity: CwEntityType, isFolder: boolean) {
  if (entity === 'issue') return <Ticket size={16} className="cw-result__icon cw-result__icon--issue" />;
  if (entity === 'page') return <FileText size={16} className="cw-result__icon cw-result__icon--page" />;
  if (entity === 'site') return <Globe size={16} className="cw-result__icon cw-result__icon--site" />;
  if (entity === 'deal') return <DollarSign size={16} className="cw-result__icon cw-result__icon--deal" />;
  if (entity === 'contact') return <User size={16} className="cw-result__icon cw-result__icon--contact" />;
  if (entity === 'company') return <Building2 size={16} className="cw-result__icon cw-result__icon--company" />;
  if (entity === 'ticket') return <MessageSquare size={16} className="cw-result__icon cw-result__icon--ticket" />;
  if (isFolder) return <Folder size={16} className="cw-result__icon" />;
  return <File size={16} className="cw-result__icon" />;
}

const PROVIDER_LABEL: Record<string, string> = {
  microsoft365: 'Microsoft 365',
  google_drive: 'Google Drive',
  box: 'Box',
  atlassian: 'Atlassian',
  hubspot: 'HubSpot',
  cm: 'Intranet',
};

/** Short modified date for the results column ("May 11, 2026"). */
export function formatModified(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const CwResultRow: FC<Props> = ({
  hit,
  active = false,
  onSelect,
  id,
  onHoverEnter,
  onHoverMove,
  onHoverLeave,
}) => {
  const providerLabel = PROVIDER_LABEL[hit.provider_type] ?? hit.provider_type;
  const subtitleParts: string[] = [];
  if (hit.parent_name) subtitleParts.push(hit.parent_name);
  if (hit.owner?.name) subtitleParts.push(`by ${hit.owner.name}`);

  return (
    <button
      type="button"
      role="option"
      id={id}
      aria-selected={active}
      className={`cw-result-row${active ? ' cw-result-row--active' : ''}`}
      onClick={(e) => onSelect(hit, e)}
      // Stop the input from losing focus when user mouses down on a result.
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onHoverEnter ? (e) => onHoverEnter(hit, e) : undefined}
      onMouseMove={onHoverMove ? (e) => onHoverMove(e) : undefined}
      onMouseLeave={onHoverLeave}
    >
      <span className="cw-result-row__icon">{renderIcon(hit.entity_type, hit.is_folder)}</span>
      <span className="cw-result-row__body">
        <span className="cw-result-row__title">{hit.file_name}</span>
        {subtitleParts.length > 0 && (
          <span className="cw-result-row__subtitle">{subtitleParts.join(' · ')}</span>
        )}
      </span>
      <span className="cw-result-row__provider">{providerLabel}</span>
      <span className="cw-result-row__modified">
        {hit.last_modified_at ? (
          <time dateTime={hit.last_modified_at} title={new Date(hit.last_modified_at).toLocaleString()}>
            <span className="sr-only">Modified </span>{formatModified(hit.last_modified_at)}
          </time>
        ) : (
          <span aria-label="Modified date unknown">—</span>
        )}
      </span>
    </button>
  );
};

export default CwResultRow;
